import { Pipe, PipeTransform } from '@angular/core';
import pluralize from 'pluralize';
import { AssetIndexTypes } from '../../../../../shared/enums/asset-index-types.enum';
import { AssetTypes } from '../../../../../shared/enums/asset-types';
import { RequestStatus } from '../../../../../shared/enums/request-status.enum';
import { RequestTypes } from '../../../../../shared/enums/request-types.enum';
import { TransactionTypes } from '../../../../../shared/enums/transaction-types';
import { Asset, CustomStudioFieldName, Field, Request, Transaction, User } from '../../../core/store/models';
import { TableTypes } from '../../../core/store/types';

@Pipe({
    name: 'canAccessAssetDetails',
    standalone: false
})
export class CanAccessAssetDetailsPipe implements PipeTransform {
    transform(assetLike, user: User) {
        if (user && user.permission && assetLike) {
            if (assetLike.division_id) {
                return user.permission.canAccessAssetDetailsInDivision(assetLike.division_id, assetLike.studio_id);
            } else {
                return user.permission.canAccessAssetDetailsInFranchise(assetLike.franchise_id, assetLike.studio_id);
            }
        }
        return false;
    }
}

@Pipe({
    name: 'getColumnFitlerTooltip',
    standalone: false
})
export class GetColumnFitlerTooltipPipe implements PipeTransform {
    transform(
        field: Field,
        tableType: TableTypes,
        customFieldNames: CustomStudioFieldName,
        selectedProjectCurrencyId: number,
        selectedProjectIdsList: number[]
    ) {
        if (field && ((field.canonical_name === 'other_cost' && !selectedProjectCurrencyId) || (field.canonical_name === 'local_cost' && tableType === TableTypes.FranchiseTable && selectedProjectIdsList.length !== 1))) {
            return `To filter this column, please select one ${customFieldNames && customFieldNames.project_id ? customFieldNames.project_id : 'Project'} from the sidenav filters ${field.canonical_name === 'other_cost' ? 'and select a currency from the Cost (Other) dropdown' : ''}`;
        }
        return null;
    }
}

@Pipe({
    name: 'getPaginatorDisplay',
    standalone: false
})
export class GetPaginatorDisplayPipe implements PipeTransform {
    transform(
        tableType: TableTypes,
        rowsPerPage: number,
        first: number,
        totalRecords: number
    ) {
        if (typeof first === 'number' && typeof rowsPerPage === 'number') {
            const assetsOrTransactions = pluralize(tableType === TableTypes.TransactionTable ? 'transaction' : 'asset', totalRecords);
            return `Results: ${totalRecords > 0 ? `${first + 1} - ${getPaginatorUpperLimit(first, totalRecords, rowsPerPage)} of ${totalRecords} ${assetsOrTransactions}` : `0 ${assetsOrTransactions}`}`;
        }
        return '';
    }
}

export function getPaginatorUpperLimit(first: number, totalRecords: number, rowsPerPage: number) {
    const pageUpperLimit: number = first + rowsPerPage;
    return pageUpperLimit > totalRecords ? totalRecords : pageUpperLimit;
}

@Pipe({
    name: 'getPluralizedAssetOrTransaction',
    standalone: false
})
export class GetPluralizedAssetOrTransactionPipe implements PipeTransform {
    transform(assetCountTotal: number, tableType: TableTypes) {
        return tableType === TableTypes.TransactionTable ? pluralize('transaction', assetCountTotal) : pluralize('asset', assetCountTotal);
    }
}

/**
 * Logic for when the Bulk Options button should be disabled.
 */
@Pipe({
    name: 'isBulkOptionsDisabled',
    standalone: false
})
export class IsBulkOptionsDisabledPipe implements PipeTransform {
    transform(
        isTransactionTable: boolean,
        selectedTransactions: Set<number>,
        selectedAssets: Set<number>,
        indexType: AssetIndexTypes,
        transactionTypesShown: TransactionTypes,
        assetTypesShown: AssetTypes,
        disabledExportButton?: boolean
    ) {
        if (isTransactionTable ? selectedTransactions.size === 0 : selectedAssets.size === 0) {
            return true; // Nothing selected
        } else if (isTransactionTable && indexType === AssetIndexTypes.Default && transactionTypesShown !== TransactionTypes.Unreconciled) {
            return true; // On anything but unreconciled view on the transaction default tab
        } else if (disabledExportButton && assetTypesShown === AssetTypes.Historical) {
            return true;
        }
        return false;
    }
}

/** Logic for when the table's caption bar with the number of selected items should be visible */
@Pipe({
    name: 'isCaptionVisible',
    standalone: false
})
export class IsCaptionVisiblePipe implements PipeTransform {
    transform(
        tableType: TableTypes,
        indexType: AssetIndexTypes,
        assetTypesShown: AssetTypes,
        transactionTypesShown: TransactionTypes,
        responsive: boolean
    ) {
        if (responsive) {
            return false; // Hide if on small widths
        } else if (tableType === TableTypes.TransactionTable) {
            if (indexType === AssetIndexTypes.Default && transactionTypesShown !== TransactionTypes.Unreconciled) {
                return false; // Hide if showing anything but unreconciled on transaction default tab
            }
            return true;
        } else if (tableType === TableTypes.FranchiseTable && indexType === AssetIndexTypes.Default && assetTypesShown === AssetTypes.Division) {
            return false; // Hide if showing division only or historical assets franchise table default tab
        }
        return true;
    }
}

@Pipe({
    name: 'isClearSelectionVisible',
    standalone: false
})
export class IsClearSelectionVisiblePipe implements PipeTransform {
    transform(
        selectAllCheckedForPage: { [page: number]: boolean },
        isTransactionTable: boolean,
        selectedTransactions: Set<number>,
        selectedAssets: Set<number>,
        isAllAssetsSelected: boolean
    ) {
        return (!checkIfAnySelectAllChecked(selectAllCheckedForPage) && (isTransactionTable ? selectedTransactions.size : selectedAssets.size)) || isAllAssetsSelected;
    }
}

@Pipe({
    name: 'isRequestConflictColumnVisible',
    standalone: false
})
export class IsRequestConflictColumnVisiblePipe implements PipeTransform {
    transform(request: Request, tableType: TableTypes) {
        return tableType === TableTypes.RequestTable && (request.status !== RequestStatus.Unsent && request.status !== RequestStatus.Closed);
    }
}

@Pipe({
    name: 'isRequestSentToColumnVisible',
    standalone: false
})
export class IsRequestSentToColumnVisiblePipe implements PipeTransform {
    transform(requestType: RequestTypes, tableType: TableTypes, finalApproversEnabled: boolean) {
        return tableType === TableTypes.RequestTable && requestType === RequestTypes.Approver && finalApproversEnabled;
    }
}

@Pipe({
    name: 'isRequestStatusColumnVisible',
    standalone: false
})
export class IsRequestStatusColumnVisiblePipe implements PipeTransform {
    transform(request: Request, tableType: TableTypes, requestType: RequestTypes) {
        return tableType === TableTypes.RequestTable && ((requestType === RequestTypes.Requester && (request.status === RequestStatus.Complete || request.status
            === RequestStatus.Closed)) || requestType !== RequestTypes.Requester);
    }
}

/** Logic for when the checkbox on assets/transactions are visible */
@Pipe({
    name: 'isRowCheckboxVisible',
    standalone: false
})
export class IsRowCheckboxVisiblePipe implements PipeTransform {
    transform(
        asset: Asset,
        tableType: TableTypes,
        indexType: AssetIndexTypes,
        assetTypesShown: AssetTypes,
        transactionTypesShown: TransactionTypes
    ) {
        if (!asset) {
            return false;
        } else if (tableType === TableTypes.FranchiseTable && asset.division_id && assetTypesShown === AssetTypes.Division && indexType === AssetIndexTypes.Default) {
            return false; // Hide for division assets on the franchise table or historical assets on the franchise table default tab
        } else if (tableType === TableTypes.TransactionTable && indexType === AssetIndexTypes.Default && transactionTypesShown !== TransactionTypes.Unreconciled) {
            return false; // Hide if showing anything but unreconciled on transaction default tab
        }
        return true;
    }
}

/** Logic for when the "Select all in search results" is visible */
@Pipe({
    name: 'isSelectAllCaptionVisible',
    standalone: false
})
export class IsSelectAllCaptionVisiblePipe implements PipeTransform {
    transform(
        isAllAssetsSelected: boolean,
        selectAllCheckedForPage: { [page: number]: boolean },
        isTransactionTable: boolean,
        selectedTransactions: Set<number>,
        selectedAssets: Set<number>,
        assetCount: number,
        transactions: Transaction[]
    ) {
        if (isTransactionTable && selectedTransactions.size === transactions.length) {
            return false; // Should not show if number of selected items is equal to total number of items
        } else if (!isTransactionTable && selectedAssets.size === assetCount) {
            return false; // Should not show if number of selected items is equal to total number of items
        }
        return !isAllAssetsSelected && checkIfAnySelectAllChecked(selectAllCheckedForPage) && (isTransactionTable ? selectedTransactions.size !== 0 : selectedAssets.size !== 0);
    }
}

export function checkIfAnySelectAllChecked(selectAllCheckedForPage: { [page: number]: boolean }) {
    return Object.keys(selectAllCheckedForPage).some(page => selectAllCheckedForPage[page]);
}

/** Logic for when the checkbox in the header is visible */
@Pipe({
    name: 'isSelectAllCheckboxVisible',
    standalone: false
})
export class IsSelectAllCheckboxVisiblePipe implements PipeTransform {
    transform(
        tableType: TableTypes,
        indexType: AssetIndexTypes,
        assetTypesShown: AssetTypes,
        transactionTypesShown: TransactionTypes
    ) {
        if (tableType === TableTypes.FranchiseTable && indexType === AssetIndexTypes.Default && assetTypesShown === AssetTypes.Division) {
            return false; // Hide if showing division only or historical assets franchise table default tab
        } else if (tableType === TableTypes.TransactionTable && indexType === AssetIndexTypes.Default && transactionTypesShown !== TransactionTypes.Unreconciled) {
            return false; // Hide if showing anything but unreconciled on transaction default tab
        }
        return true;
    }
}

@Pipe({
    name: 'truncateAssetField',
    standalone: false
})
export class TruncateAssetFieldPipe implements PipeTransform {
    transform(asset: Asset, canonicalName: string, length = 150) {
        let string = '';
        if (asset && asset.displayFields && canonicalName && asset.displayFields[canonicalName]) {
            string += asset.displayFields[canonicalName].toString().substring(0, length);
            if (asset.displayFields[canonicalName].length > length) {
                string += '...';
            }
        }
        return string;
    }
}
